import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Textarea,
  useToast,
} from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createTasksApi } from "../api";
import {
  allowedTaskActions,
  dueLabel,
  entityDrilldownPath,
  formatLabel,
  formatWhen,
  isClosedStatus,
  priorityTone,
  SOURCE_LABEL,
  taskStatusTone,
} from "../labels";
import type { ActivityEntry, Me, TaskAction, TaskDto, TaskLinkDto } from "../types";
import { AddTaskLinkDialog, EditTaskDialog, TaskActionDialog } from "../components/TaskDialogs";

/** Actions that need no note: run straight away. */
const IMMEDIATE: TaskAction[] = ["start", "unblock"];

const ACTION_LABEL: Record<TaskAction, string> = {
  assign: "Assign",
  start: "Start",
  block: "Mark blocked",
  unblock: "Unblock",
  mark_pending_approval: "Waiting on approval",
  complete: "Complete",
  cancel: "Cancel task",
  reopen: "Reopen",
};

function describeEvent(e: ActivityEntry): string {
  if (e.kind === "comment") return "commented";
  const map: Record<string, string> = {
    "task.created": "created the task",
    "task.assigned": "assigned it",
    "task.blocked": "marked it blocked",
    "task.completed": "completed it",
    "task.updated": "edited it",
    "task_link.created": "linked a record",
    "task_link.deleted": "removed a link",
    "task.bulk_assign": "assigned it (bulk)",
  };
  if (map[e.action]) return map[e.action]!;
  if (e.from_status && e.to_status && e.from_status !== e.to_status) {
    return `moved it from ${formatLabel(e.from_status)} to ${formatLabel(e.to_status)}`;
  }
  return formatLabel(e.action.replace("task.", ""));
}

export function TaskDetailPage() {
  const { taskId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const client = useApiClient();
  const api = useMemo(() => createTasksApi(client), [client]);

  const [task, setTask] = useState<TaskDto | null>(null);
  const [links, setLinks] = useState<TaskLinkDto[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; status?: number } | null>(null);
  const [action, setAction] = useState<TaskAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Task Center", to: "/app/tasks" },
    { label: task?.title ?? "Task" },
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, l, a] = await Promise.all([api.getTask(taskId), api.listTaskLinks(taskId), api.activity(taskId)]);
      setTask(t.task);
      setLinks(l.links);
      setActivity(a.activity);
    } catch (err) {
      setError({ message: apiErrorMessage(err), ...(err instanceof ApiError ? { status: err.status } : {}) });
    } finally {
      setLoading(false);
    }
  }, [api, taskId]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null));
  }, [api]);

  async function runAction(a: TaskAction, body: Record<string, unknown>) {
    setBusy(true);
    setDialogError(null);
    try {
      await api.applyTaskAction(taskId, a, body);
      toast.push({ title: `${ACTION_LABEL[a]}: done`, tone: "success" });
      setAction(null);
      await load();
    } catch (err) {
      setDialogError(apiErrorMessage(err));
      if (IMMEDIATE.includes(a)) toast.push({ title: "Could not update the task", description: apiErrorMessage(err), tone: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function assignToMe() {
    if (!me || !task) return;
    // Re-assigning an already-assigned task is an edit, not a lifecycle step.
    if (task.status === "created" || task.status === "reopened") {
      await runAction("assign", { owner_user_id: me.user_id });
      return;
    }
    setBusy(true);
    try {
      await api.patchTask(taskId, { owner_user_id: me.user_id });
      toast.push({ title: "Assigned to you", tone: "success" });
      await load();
    } catch (err) {
      toast.push({ title: "Could not assign", description: apiErrorMessage(err), tone: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function postComment() {
    const body = comment.trim();
    if (!body) return;
    setCommentBusy(true);
    try {
      await api.addComment(taskId, body);
      setComment("");
      const a = await api.activity(taskId);
      setActivity(a.activity);
    } catch (err) {
      toast.push({ title: "Comment not saved", description: apiErrorMessage(err), tone: "danger" });
    } finally {
      setCommentBusy(false);
    }
  }

  if (loading && !task) return <LoadingState message="Loading task…" />;
  if (error && !task) {
    return (
      <EmptyState
        title={error.status === 404 ? "Task not found" : error.status === 403 ? "You don't have access to this task" : "Could not load the task"}
        description={error.status === 403 ? "The Task Center needs the Operations › View permission." : error.message}
        action={<Button variant="secondary" onClick={() => void navigate("/app/tasks")}>Back to Task Center</Button>}
      />
    );
  }
  if (!task) return null;

  const closed = isClosedStatus(task.status);
  const actions = allowedTaskActions(task.status);
  const sourcePath = task.source_type && task.source_id ? entityDrilldownPath(task.source_type, task.source_id) : null;
  const canReassign = !closed && task.owner_user_id !== me?.user_id;

  return (
    <Stack gap={24}>
      <SectionHeader
        title={task.title ?? "Untitled task"}
        description={
          task.origin === "system"
            ? "Opened automatically from a real event. It closes itself when the source is handled."
            : task.created_by_name
              ? `Created by ${task.created_by_name}${task.created_at ? ` · ${formatWhen(task.created_at)}` : ""}`
              : undefined
        }
        actions={
          <Flex gap={8} wrap>
            {canReassign && me ? (
              <Button onClick={() => void assignToMe()} disabled={busy}>
                Assign to me
              </Button>
            ) : null}
            {actions
              .filter((a) => a !== "assign" || !canReassign)
              .map((a) => (
                <Button
                  key={a}
                  variant={a === "cancel" ? "danger" : a === "complete" ? "accent" : "secondary"}
                  disabled={busy}
                  onClick={() => (IMMEDIATE.includes(a) ? void runAction(a, {}) : setAction(a))}
                >
                  {ACTION_LABEL[a]}
                </Button>
              ))}
            {!closed ? (
              <Button variant="ghost" onClick={() => setAction("assign")} disabled={busy}>
                {task.owner_user_id ? "Reassign" : "Assign to…"}
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </Flex>
        }
      />

      <Flex gap={16} wrap align="start">
        <Stack gap={16} style={{ flex: "2 1 480px", minWidth: 0 }}>
          <Card header={<Text role="title" as="h3">Details</Text>}>
            <Stack gap={12}>
              <Flex gap={8} wrap>
                <StatusChip tone={taskStatusTone(task.status)}>{formatLabel(task.status)}</StatusChip>
                <StatusChip tone={priorityTone(task.priority)}>{formatLabel(task.priority)} priority</StatusChip>
                {task.is_overdue ? <StatusChip tone="danger">Overdue</StatusChip> : null}
              </Flex>
              {task.description ? (
                <Text role="body" as="p" style={{ whiteSpace: "pre-wrap" }}>
                  {task.description}
                </Text>
              ) : (
                <Text role="small" color="muted" as="p">
                  No details. Use Edit to add context for whoever picks this up.
                </Text>
              )}
              {task.outcome ? (
                <Alert tone={task.status === "completed" ? "success" : "info"} title="Outcome">
                  {task.outcome}
                </Alert>
              ) : null}
            </Stack>
          </Card>

          <Card header={<Text role="title" as="h3">Activity</Text>}>
            <Stack gap={12}>
              {activity.length === 0 ? (
                <Text role="small" color="muted" as="p">No activity yet.</Text>
              ) : (
                activity.map((e) => (
                  <Stack key={`${e.kind}-${e.id}`} gap={4}>
                    <Text role="small" as="p">
                      <strong>{e.actor_name ?? (e.actor_user_id ? "Staff member" : "HUDDL (automatic)")}</strong>{" "}
                      {describeEvent(e)}
                      <Text role="caption" color="muted" as="span">{` · ${formatWhen(e.at)}`}</Text>
                    </Text>
                    {e.note ? (
                      <Text role="small" color={e.kind === "comment" ? "primary" : "secondary"} as="p" style={{ whiteSpace: "pre-wrap", paddingLeft: 12, borderLeft: "2px solid var(--color-border-subtle)" }}>
                        {e.note}
                      </Text>
                    ) : null}
                  </Stack>
                ))
              )}
              <Textarea
                label="Add a comment"
                value={comment}
                maxLength={4000}
                rows={3}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void postComment();
                }}
              />
              <Flex gap={8}>
                <Button size="sm" onClick={() => void postComment()} disabled={commentBusy || !comment.trim()}>
                  {commentBusy ? "Posting…" : "Comment"}
                </Button>
              </Flex>
            </Stack>
          </Card>
        </Stack>

        <Stack gap={16} style={{ flex: "1 1 280px", minWidth: 0 }}>
          <Card header={<Text role="title" as="h3">About</Text>}>
            <Stack gap={8}>
              <Row label="Owner" value={task.owner_user_id ? (task.owner_user_id === me?.user_id ? "Me" : (task.owner_name ?? "Staff member")) : "Unassigned"} />
              <Row label="Team" value={formatLabel(task.owning_department)} />
              <Row label="Due" value={task.due_at ? `${formatWhen(task.due_at)} (${dueLabel(task.due_at, closed)})` : "No due date"} />
              <Row label="Created" value={formatWhen(task.created_at)} />
              {task.completed_at ? <Row label="Completed" value={formatWhen(task.completed_at)} /> : null}
            </Stack>
          </Card>

          {task.source_type && task.source_id ? (
            <Card header={<Text role="title" as="h3">Came from</Text>}>
              <Stack gap={8}>
                <Text role="body" as="p">{SOURCE_LABEL[task.source_type] ?? formatLabel(task.source_type)}</Text>
                {sourcePath ? (
                  <Button variant="secondary" onClick={() => void navigate(sourcePath)}>
                    Open {SOURCE_LABEL[task.source_type]?.toLowerCase() ?? "record"}
                  </Button>
                ) : (
                  <Text role="caption" color="muted" as="p">Open it from the related record below.</Text>
                )}
              </Stack>
            </Card>
          ) : null}

          <Card
            header={
              <Flex gap={8} justify="space-between" align="center">
                <Text role="title" as="h3">Linked records</Text>
                <Button size="sm" variant="ghost" onClick={() => setLinkOpen(true)}>Add</Button>
              </Flex>
            }
          >
            <Stack gap={8}>
              {links.length === 0 ? (
                <Text role="small" color="muted" as="p">Nothing linked.</Text>
              ) : (
                links.map((l) => {
                  const path = entityDrilldownPath(l.entity_type, l.entity_id);
                  return (
                    <Flex key={l.id} gap={8} align="center" justify="space-between">
                      <Text role="small" as="span">
                        {SOURCE_LABEL[l.entity_type] ?? formatLabel(l.entity_type)}{" "}
                        <Text role="caption" color="muted" as="span">({formatLabel(l.link_type)})</Text>
                      </Text>
                      <Flex gap={4}>
                        {path ? <Button size="sm" variant="ghost" onClick={() => void navigate(path)}>Open</Button> : null}
                        {l.link_type !== "source" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              try {
                                await api.deleteTaskLink(taskId, l.id);
                                await load();
                              } catch (err) {
                                toast.push({ title: "Could not remove link", description: apiErrorMessage(err), tone: "danger" });
                              }
                            }}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </Flex>
                    </Flex>
                  );
                })
              )}
            </Stack>
          </Card>
        </Stack>
      </Flex>

      <TaskActionDialog
        open={action !== null}
        action={action}
        meId={me?.user_id}
        busy={busy}
        error={dialogError}
        onClose={() => {
          setAction(null);
          setDialogError(null);
        }}
        onSubmit={async (body) => {
          if (!action) return;
          // Assign is only a lifecycle step from created/reopened; otherwise it is a reassignment.
          if (action === "assign" && !(task.status === "created" || task.status === "reopened")) {
            setBusy(true);
            try {
              await api.patchTask(taskId, { owner_user_id: body.owner_user_id ?? null });
              toast.push({ title: "Reassigned", tone: "success" });
              setAction(null);
              await load();
            } catch (err) {
              setDialogError(apiErrorMessage(err));
            } finally {
              setBusy(false);
            }
            return;
          }
          await runAction(action, body);
        }}
      />

      <EditTaskDialog
        open={editOpen}
        task={task}
        busy={busy}
        error={dialogError}
        onClose={() => {
          setEditOpen(false);
          setDialogError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setDialogError(null);
          try {
            await api.patchTask(taskId, body);
            toast.push({ title: "Saved", tone: "success" });
            setEditOpen(false);
            await load();
          } catch (err) {
            setDialogError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <AddTaskLinkDialog
        open={linkOpen}
        busy={busy}
        error={dialogError}
        onClose={() => {
          setLinkOpen(false);
          setDialogError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setDialogError(null);
          try {
            await api.createTaskLink(taskId, body);
            setLinkOpen(false);
            await load();
          } catch (err) {
            setDialogError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Flex gap={8} justify="space-between">
      <Text role="small" color="secondary" as="span">{label}</Text>
      <Text role="small" as="span" style={{ textAlign: "right" }}>{value}</Text>
    </Flex>
  );
}
